'use client';

import { twMerge } from '@shared/themes';
import { NotificationDot } from '../../../../NotificationDot/index';
import type { ContentSwitcherButtonProps } from '../../../types';

function ContentSwitcherButton<T>({ option, checked, onClick, size, disabled = false, className }: ContentSwitcherButtonProps<T>) {
  // Disabled state can be set at the option level or the group level.
  const isDisabled = option.disabled || disabled;

  const sizeClasses = size === 'small' ? 'h-8 px-4 py-1' : 'h-10 px-6 py-2';
  const classes = {
    default: `relative flex justify-center items-center w-fit cursor-pointer paragraph-explanatory-strong rounded-full text-foreground bg-button-background-inverse ${sizeClasses}`,
    hover: !isDisabled && `hover:bg-button-background-inverse-hover`,
    active: !isDisabled && `active:bg-button-background-active active:text-foreground-inverse`,
    selected: `data-[checked=true]:bg-button-background data-[checked=true]:text-foreground-inverse`,
    disabled: `disabled:opacity-50 disabled:cursor-not-allowed`,
  };
  const classList = twMerge(classes.default, classes.hover, classes.active, classes.selected, classes.disabled, className);

  return (
    <button
      role="radio"
      value={option.value?.toString() ?? ''}
      onClick={() => onClick(option)}
      onTouchStart={() => onClick(option)}
      className={classList}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      data-checked={checked}
      aria-checked={checked}
      data-track-name={option.label}
      data-track-checked={checked}
      {...option.trackingProps}
      data-testid={option['data-testid']}
    >
      {option?.label}
      {option.showNotification && <NotificationDot className="absolute top-4 right-2" data-testid="content-switcher-button-notification-dot" />}
    </button>
  );
}

export { ContentSwitcherButton };
